import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ExternalLink, Copy, Check } from "lucide-react";
import useRamp from "../../../hooks/useRamp";
import useNetworks from "../../../hooks/useNetworks";
import useAPI from "../../../hooks/useAPI";
import RampEventModal from "../../../components/modals/RampEventModal";
import useModal from "../../../hooks/useModal";

export default function RampSessions() {
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [sessions, setSessions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [networks, setNetworks] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);

  const rampEventModal = useModal("rampEventModal");
  const { rampSessions } = useRamp();
  const { adminNetworks } = useNetworks();
  const { getCurrencies } = useAPI();

  const getTypeColor = (type) => {
    switch (type) {
      case "onramp":
        return "bg-emerald-100 text-emerald-800 border-emerald-200";
      case "offramp":
        return "bg-rose-100 text-rose-800 border-rose-200";
      case "ars":
        return "bg-blue-100 text-blue-800 border-blue-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "N/A";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatAmount = (amount) => {
    return amount !== null && amount !== undefined
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        }).format(amount)
      : "N/A";
  };

  const getNetworkDisplay = (networkId) => {
    const network = networks.find((item) => String(item.id) === String(networkId));
    if (!network) return null;
    return {
      name: network.name || "N/A",
      chainId: network.chainId ?? "N/A",
      explorerUrl: network.explorerUrl || network.explorer_url || null,
    };
  };

  const getCurrencyDisplay = (currencyId) => {
    const currency = currencies.find((item) => String(item.id) === String(currencyId));
    if (!currency) return null;
    return {
      symbol: currency.symbol || "",
      name: currency.name || "N/A",
    };
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const generatePageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 2);
    const endPage = Math.min(totalPages, currentPage + 2);

    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push("...");
    }

    for (let i = startPage; i <= endPage; i++) pages.push(i);

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...");
      pages.push(totalPages);
    }

    return pages;
  };

  const getSessionStatus = (session) => {
    const hasAmount = session.amount !== null && session.amount !== undefined;
    if (!hasAmount && !session.checkoutContractId) return "Processing";
    if (hasAmount) return "Completed";
    if (session.checkoutContractId && !hasAmount) return "Cancelled";
    return "Processing";
  };

  useEffect(() => {
    const fetchNetworks = async () => {
      try {
        const response = await adminNetworks();
        setNetworks(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error(error);
        setNetworks([]);
      }
    };
    fetchNetworks();
  }, []);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const response = await getCurrencies();
        setCurrencies(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error(error);
        setCurrencies([]);
      }
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const response = await rampSessions(currentPage, limit);
        setSessions(response?.sessions || []);
        setTotal(response?.total || 0);
        setTotalPages(response?.totalPages || 0);
      } catch (error) {
        console.error(error);
        setSessions([]);
        setTotal(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };
    fetchSessions();
  }, [currentPage, limit]);

  return (
    <div className="2xl:p-6 p-3">
      <div className="2xl:space-y-6 space-y-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 2xl:p-6 p-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="2xl:text-2xl text-lg font-bold text-gray-900">Ramp Sessions</h1>
              <p className="text-gray-600 mt-1 text-sm">
                Manage and monitor all ramp transactions • {total} total sessions
              </p>
            </div>
            <div className="flex items-center gap-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-sm text-gray-500">Active Sessions</div>
                <div className="text-2xl font-bold text-gray-900">{total}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {loading
                ? "Loading sessions..."
                : total === 0
                  ? "Showing 0-0 of 0 sessions"
                  : `Showing ${(currentPage - 1) * limit + 1}-${Math.min(currentPage * limit, total)} of ${total} sessions`}
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show:</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {[10, 20, 50, 100].map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <div className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </div>
            </div>
          </div>
        </div>

        {/* Sessions Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full divide-y divide-gray-200 table-fixed">
              <thead className="bg-gray-50">
                <tr>
                  <th className="2xl:px-6 px-3 2xl:py-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[18%]">
                    Session Details
                  </th>
                  <th className="2xl:px-6 px-3 2xl:py-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[16%]">
                    Type & Amount
                  </th>
                  <th className="2xl:px-6 px-3 2xl:py-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[14%]">
                    Token/Network
                  </th>
                  <th className="2xl:px-6 px-3 2xl:py-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[12%]">
                    Payment
                  </th>
                  <th className="2xl:px-6 px-3 2xl:py-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[16%]">
                    Dates
                  </th>
                  <th className="2xl:px-6 px-3 2xl:py-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[12%]">
                    Status
                  </th>
                  <th className="2xl:px-6 px-3 2xl:py-4 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-[12%]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading
                  ? Array.from({ length: limit }).map((_, idx) => (
                      <tr key={idx} className="animate-pulse">
                        <td colSpan={7} className="px-6 py-4 h-10 bg-gray-100" />
                      </tr>
                    ))
                  : sessions.length === 0
                  ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        No sessions found
                      </td>
                    </tr>
                  )
                  : sessions.map((session) => {
                      const networkDisplay = getNetworkDisplay(session.networkId);
                      const currencyDisplay = getCurrencyDisplay(session.currencyId);

                      return (
                      <tr
                        key={session.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedSession(session);
                          rampEventModal.open();
                        }}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-sm font-medium text-gray-900">
                              {session.id.slice(0, 8)}...
                            </span>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                copyToClipboard(session.id, session.id);
                              }}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                            >
                              {copiedId === session.id ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                            </button>
                          </div>
                          <div className="text-xs text-gray-500 truncate">User ID: {session.userId || "N/A"}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeColor(session.type)}`}>
                            {session.type?.toUpperCase() || "N/A"}
                          </span>
                          <div className="text-lg font-semibold text-gray-900">{formatAmount(session.amount)}</div>
                          <div className="text-xs text-gray-500">
                            {currencyDisplay
                              ? `${currencyDisplay.symbol ? `${currencyDisplay.symbol} • ` : ""}${currencyDisplay.name}`
                              : "N/A"}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {networkDisplay ? (
                            <div>
                              <div className="text-sm font-medium text-gray-900">{networkDisplay.name}</div>
                              <div className="text-xs text-gray-500">Chain ID: {networkDisplay.chainId}</div>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </td>
                        <td className="px-6 py-4">{session.paymentMethod?.replace("_", " ").toUpperCase() || "N/A"}</td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{formatDate(session.createdAt)}</div>
                          <div className="text-xs text-gray-500">Updated: {formatDate(session.updatedAt)}</div>
                        </td>
                        <td className="px-6 py-4">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-gray-100 text-gray-800">
                            {getSessionStatus(session)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            {networkDisplay?.explorerUrl ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.open(networkDisplay.explorerUrl, "_blank", "noopener,noreferrer");
                                }}
                                className="inline-flex items-center px-2 py-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                                title="Open in blockchain explorer"
                              >
                                <ExternalLink className="h-6 w-6" />
                              </button>
                            ) : (
                              "N/A"
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {(currentPage - 1) * limit + 1} to {Math.min(currentPage * limit, total)} of {total} results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4 mr-1" /> Previous
              </button>
              {generatePageNumbers().map((page, idx) =>
                page === "..." ? (
                  <span key={idx} className="px-3 py-2 text-gray-500">
                    ...
                  </span>
                ) : (
                  <button
                    key={idx}
                    onClick={() => goToPage(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      page === currentPage ? "bg-blue-600 text-white shadow-sm" : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                )
              )}
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      <RampEventModal name="rampEventModal" session={selectedSession} />
    </div>
  );
}
